import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

export type Mode = 'dokumendid' | 'terminid' | 'moisted'

export interface TermBaseState {
  mode: Mode
  q: string
  lang: string
  /** Concepts only: a language the concept must NOT have — the gap finder. */
  missingLang: string
  domain: string
  abbrOnly: boolean
  /** Selected term, identified by value+lang (the API has no stable slug). */
  term: string
  termLang: string
  conceptId: number | null
  /** Which collection is being browsed; null = default. */
  collectionId: number | null
  /**
   * Selected documents, in selection order. They double as the document
   * filter on the other tabs; the last one is the document open in the text
   * pane. Written as one comma-separated `dok` param.
   */
  documentIds: number[]
  /** The open document — the last of `documentIds`. Patching it replaces the selection. */
  documentId: number | null
  /** Page to open the selected document at — set when jumping from a mention. */
  page: number | null
}

/** Query-param names match the Panel view's, so existing shared links still resolve. */
const KEYS = {
  mode: 'vaade',
  q: 'q',
  lang: 'keel',
  missingLang: 'puudub',
  domain: 'valdkond',
  abbrOnly: 'lyhendid',
  term: 'termin',
  termLang: 'tkeel',
  conceptId: 'moiste',
  collectionId: 'kogu',
  documentId: 'dok',
  page: 'lk',
} as const

/**
 * The query string a link to this view needs. Same key names `patch` writes,
 * so a link and an in-place change land on the same URL — which is what lets
 * the term base be opened in a second tab at exactly the spot you were at.
 */
export function termBaseSearch(next: Partial<TermBaseState>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(next)) {
    const key = k === 'documentIds' ? KEYS.documentId : KEYS[k as keyof typeof KEYS]
    if (!key) continue
    const empty =
      v === '' || v === null || v === undefined || v === false || v === 0 ||
      (Array.isArray(v) && v.length === 0)
    if (empty) continue
    p.set(key, v === true ? '1' : Array.isArray(v) ? v.join(',') : String(v))
  }
  const qs = p.toString()
  return qs ? `?${qs}` : ''
}

export function useTermBaseState() {
  const [params, setParams] = useSearchParams()

  const state = useMemo<TermBaseState>(() => {
    const raw = params.get(KEYS.mode)
    // 'kandidaadid' was the old candidate layer; those links open plain Terminid.
    const mode: Mode =
      raw === 'moisted' ? 'moisted' : raw === 'dokumendid' ? 'dokumendid' : 'terminid'
    const cid = Number(params.get(KEYS.conceptId))
    const coll = Number(params.get(KEYS.collectionId))
    const documentIds = [
      ...new Set(
        (params.get(KEYS.documentId) ?? '')
          .split(',')
          .map(Number)
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ]
    const page = Number(params.get(KEYS.page))
    return {
      mode,
      q: params.get(KEYS.q) ?? '',
      lang: params.get(KEYS.lang) ?? '',
      missingLang: params.get(KEYS.missingLang) ?? '',
      domain: params.get(KEYS.domain) ?? '',
      abbrOnly: params.get(KEYS.abbrOnly) === '1',
      term: params.get(KEYS.term) ?? '',
      termLang: params.get(KEYS.termLang) ?? '',
      conceptId: Number.isFinite(cid) && cid > 0 ? cid : null,
      collectionId: Number.isFinite(coll) && coll > 0 ? coll : null,
      documentIds,
      documentId: documentIds[documentIds.length - 1] ?? null,
      page: Number.isFinite(page) && page > 0 ? page : null,
    }
  }, [params])

  const patch = useCallback(
    (next: Partial<TermBaseState>, { replace = true }: { replace?: boolean } = {}) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          for (const [k, v] of Object.entries(next)) {
            // Both document fields write the same param: a single id is a
            // selection of one.
            const key = k === 'documentIds' ? KEYS.documentId : KEYS[k as keyof typeof KEYS]
            if (!key) continue
            const empty =
              v === '' ||
              v === null ||
              v === undefined ||
              v === false ||
              v === 0 ||
              (Array.isArray(v) && v.length === 0)
            if (empty) p.delete(key)
            else p.set(key, v === true ? '1' : Array.isArray(v) ? v.join(',') : String(v))
          }
          return p
        },
        { replace },
      )
    },
    [setParams],
  )

  /** Selecting a term is a navigation step, so it gets a real history entry. */
  const selectTerm = useCallback(
    (value: string, lang: string) => patch({ term: value, termLang: lang }, { replace: false }),
    [patch],
  )

  const clearFilters = useCallback(
    () => patch({ q: '', lang: '', domain: '', abbrOnly: false }),
    [patch],
  )

  return { state, patch, selectTerm, clearFilters }
}
